import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import apiClient from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// 17 sep 2026 (Augusto: "Copia el asistente de ayuda de Capital Optima").
// Mismo diseño que allá, con las decisiones que él ya tomó en ese
// producto: botón flotante "?" (no un ítem más del menú -- el sidebar de
// PCM ya está lleno), dos pestañas (Manual y Pregunta), la IA SOLO explica
// cómo usar la app y nunca ve datos del cliente, y modelo Haiku.
//
// La única fuente de verdad del manual es el backend
// (app/core/help_manual.py): esta pantalla lo pide por GET /help/manual y
// el asistente recibe exactamente ese mismo texto como contexto. Si el
// manual viviera acá en el front, tarde o temprano se desincronizaría del
// que ve la IA, y el síntoma sería la IA explicando pantallas que ya no
// existen.

// Qué tema del manual corresponde a la pantalla donde está parado el
// cliente. Se destaca arriba de la lista para que lo primero que vea sea
// la ayuda de lo que tiene delante.
const TEMA_POR_RUTA = [
  ['/accounts', 'cuentas'],
  ['/transactions', 'transacciones'],
  ['/categorias', 'categorias'],
  ['/debts', 'deudas'],
  ['/budgets', 'presupuestos'],
  ['/recurring', 'recurrentes'],
  ['/calendar', 'calendario'],
  ['/reports', 'informes'],
  ['/subscription', 'planes'],
  ['/voice', 'whatsapp-voz'],
  ['/profile', 'perfil'],
  ['/consultant', 'consulta'],
  ['/welcome', 'que-es'],
  ['/dashboard', 'que-es'],
];

const PANEL_ANCHO = 430;

const HelpAssistant = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const location = useLocation();

  const [abierto, setAbierto] = useState(false);
  const [tab, setTab] = useState('manual');
  const [temas, setTemas] = useState([]);
  const [errorManual, setErrorManual] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const [pregunta, setPregunta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorChat, setErrorChat] = useState(null);
  const [turnos, setTurnos] = useState([]); // { rol: 'user'|'assistant', texto }
  const finDelChatRef = useRef(null);

  // El manual se pide una sola vez, y recién cuando el cliente abre el
  // panel: no tiene sentido descargarlo en cada carga de la app para algo
  // que la mayoría de sesiones no va a abrir.
  useEffect(() => {
    if (!abierto || temas.length > 0) return;
    apiClient
      .get('/help/manual')
      .then((res) => setTemas(res.data?.temas || []))
      .catch(() => setErrorManual(true));
  }, [abierto, temas.length]);

  useEffect(() => {
    if (tab === 'chat' && finDelChatRef.current) {
      finDelChatRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [turnos, enviando, tab]);

  const temaDeLaPantalla = useMemo(() => {
    const match = TEMA_POR_RUTA.find(([ruta]) => location.pathname.startsWith(ruta));
    if (!match) return null;
    return temas.find((t) => t.id === match[1]) || null;
  }, [location.pathname, temas]);

  // El buscador mira título, etiquetas y cuerpo -- alguien que no conoce
  // el vocabulario de la app busca por el problema ("me alcanza", "tarjeta")
  // y eso casi nunca está en el título.
  const temasFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    // El tema de la pantalla actual se muestra destacado arriba; se excluye
    // de la lista de abajo para que no parezca duplicado.
    const resto = temas.filter((t) => !temaDeLaPantalla || t.id !== temaDeLaPantalla.id);
    if (!q) return resto;
    return resto.filter((t) =>
      `${t.titulo} ${(t.tags || []).join(' ')} ${t.cuerpo}`.toLowerCase().includes(q)
    );
  }, [temas, busqueda, temaDeLaPantalla]);

  const enviarPregunta = useCallback(
    async (textoDirecto) => {
      const texto = (textoDirecto ?? pregunta).trim();
      if (!texto || enviando) return;
      setErrorChat(null);
      setEnviando(true);
      const historial = turnos.slice(-6);
      setTurnos((prev) => [...prev, { rol: 'user', texto }]);
      setPregunta('');
      try {
        const res = await apiClient.post('/help/ask', {
          pregunta: texto,
          idioma: language,
          historial,
        });
        setTurnos((prev) => [...prev, { rol: 'assistant', texto: res.data?.respuesta || '' }]);
      } catch (err) {
        setErrorChat(
          err?.response?.data?.detail ||
            (isSpanish
              ? 'No se pudo consultar al asistente. Inténtalo de nuevo.'
              : 'Could not reach the assistant. Please try again.')
        );
      } finally {
        setEnviando(false);
      }
    },
    [pregunta, enviando, turnos, language, isSpanish]
  );

  const ejemplos = isSpanish
    ? [
        '¿Cómo registro un gasto?',
        '¿Qué es una cuenta de reserva?',
        '¿Cómo activo los recordatorios de deuda?',
        '¿Qué incluye el plan Pro?',
      ]
    : [
        'How do I record an expense?',
        'What is a reserve account?',
        'How do I turn on debt reminders?',
        "What's included in the Pro plan?",
      ];

  const abrirEnChat = (tema) => {
    setTab('chat');
    setPregunta(
      isSpanish ? `Sobre "${tema.titulo}": ` : `About "${tema.titulo}": `
    );
  };

  const renderTema = (tema, destacado = false) => (
    <Accordion
      key={tema.id}
      defaultExpanded={destacado}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: destacado ? 'primary.main' : 'divider',
        borderRadius: 1,
        mb: 1,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2" fontWeight={600}>
          {tema.titulo}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
          {tema.cuerpo}
        </Typography>
        <Button size="small" sx={{ mt: 1 }} onClick={() => abrirEnChat(tema)}>
          {isSpanish ? 'Preguntar sobre esto' : 'Ask about this'}
        </Button>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <>
      {/* El botón se esconde mientras el panel está abierto: si no, queda
          flotando encima del propio panel. */}
      {!abierto && (
        <Fab
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          aria-label={isSpanish ? 'Ayuda' : 'Help'}
          onClick={() => setAbierto(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (t) => t.zIndex.drawer - 1 }}
        >
          <HelpOutlineIcon />
        </Fab>
      )}

      <Drawer
        anchor="right"
        open={abierto}
        onClose={() => setAbierto(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: PANEL_ANCHO } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
            <Typography variant="h6">{isSpanish ? 'Ayuda' : 'Help'}</Typography>
            <IconButton onClick={() => setAbierto(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />

          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth">
            <Tab value="manual" label={isSpanish ? 'Manual' : 'Manual'} />
            <Tab value="chat" label={isSpanish ? 'Pregunta' : 'Ask'} />
          </Tabs>
          <Divider />

          {tab === 'manual' && (
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={isSpanish ? 'Buscar en el manual...' : 'Search the manual...'}
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                sx={{ mb: 2 }}
              />

              {errorManual && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {isSpanish
                    ? 'No se pudo cargar el manual. Revisa tu conexión.'
                    : 'Could not load the manual. Check your connection.'}
                </Alert>
              )}

              {temaDeLaPantalla && !busqueda && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="primary" fontWeight={600}>
                    {isSpanish ? 'SOBRE ESTA PANTALLA' : 'ABOUT THIS SCREEN'}
                  </Typography>
                  <Box sx={{ mt: 1 }}>{renderTema(temaDeLaPantalla, true)}</Box>
                  <Divider sx={{ my: 2 }} />
                </Box>
              )}

              {temasFiltrados.map((tema) => renderTema(tema))}

              {!errorManual && temas.length > 0 && temasFiltrados.length === 0 && busqueda && (
                <Typography variant="body2" color="text.secondary">
                  {isSpanish
                    ? 'Nada en el manual con esa palabra. Prueba preguntándolo en la pestaña "Pregunta".'
                    : 'Nothing in the manual for that word. Try asking it in the "Ask" tab.'}
                </Typography>
              )}
            </Box>
          )}

          {tab === 'chat' && (
            <>
              <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                {turnos.length === 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {isSpanish
                        ? 'Pregúntame cómo usar PCM. No veo tus cuentas ni tus transacciones, así que no te puedo decir cuánto gastaste — para eso están las pantallas de la app.'
                        : "Ask me how to use PCM. I can't see your accounts or transactions, so I can't tell you how much you spent — the app's screens are for that."}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {ejemplos.map((ej) => (
                        <Chip key={ej} label={ej} size="small" onClick={() => enviarPregunta(ej)} />
                      ))}
                    </Box>
                  </>
                )}

                {turnos.map((turno, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      bgcolor: turno.rol === 'user' ? 'action.hover' : 'background.paper',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {turno.rol === 'user' ? (isSpanish ? 'Tú' : 'You') : 'PCM'}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                      {turno.texto}
                    </Typography>
                  </Paper>
                ))}

                {enviando && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {isSpanish ? 'Pensando...' : 'Thinking...'}
                    </Typography>
                  </Box>
                )}

                {errorChat && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errorChat}
                  </Alert>
                )}

                <div ref={finDelChatRef} />
              </Box>

              <Divider />
              <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder={isSpanish ? 'Escribe tu pregunta...' : 'Type your question...'}
                  value={pregunta}
                  onChange={(e) => setPregunta(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      enviarPregunta();
                    }
                  }}
                />
                {/* flexShrink: 0 -- sin esto el botón se estira y deja el
                    campo de texto en una rendija (el mismo bug que apareció
                    en CapitalOptima al construir este panel). */}
                <Button
                  variant="contained"
                  onClick={() => enviarPregunta()}
                  disabled={enviando || !pregunta.trim()}
                  sx={{ flexShrink: 0, minWidth: 0, px: 2 }}
                >
                  <SendIcon fontSize="small" />
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default HelpAssistant;
